import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import type {AppPayload} from '@github-ui/issues-shared/app-payload'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useNavigate} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {MilestoneIcon, TagIcon} from '@primer/octicons-react'
import {Button, Tooltip} from '@primer/react'

import {BUTTON_LABELS} from '../../constants/buttons'
import styles from './SearchBarActions.module.css'

type SearchBarActionsProps = {
  isOwnerEnterpriseManaged: boolean
  isWritable: boolean | null
  /** When false, shows the New issue button as disabled with a tooltip. Defaults to true. */
  viewerCanCreateIssues?: boolean
  /**
   * Where this is rendered. `searchBar` (default) renders Labels/Milestones buttons and
   * returns null when `repo_issues_sidebar_layout` is on (the new page header takes over).
   * `pageHeader` skips both behaviors so the same buttons can render in the page header.
   */
  variant?: 'searchBar' | 'pageHeader'
}

export function SearchBarActions({
  isOwnerEnterpriseManaged,
  isWritable,
  viewerCanCreateIssues = true,
  variant = 'searchBar',
}: SearchBarActionsProps) {
  const navigate = useNavigate()
  const {currentUser} = useUser()
  const {scoped_repository, current_user_settings} = useAppPayload<AppPayload>()

  const isEmuUserInNonOwnedRepo = currentUser != null && !!currentUser?.is_emu && !isOwnerEnterpriseManaged

  const hideCreateButton = isWritable === false || isEmuUserInNonOwnedRepo

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  // In the legacy SearchBar slot, defer to the new page header when it owns these actions.
  if (variant === 'searchBar' && sidebarLayoutEnabled) return null

  const showLabelsAndMilestones = variant === 'searchBar'
  const repoPath = scoped_repository ? `${scoped_repository.owner}/${scoped_repository.name}` : null
  const labelsPath = repoPath ? `/${repoPath}/labels` : null
  const milestonesPath = repoPath ? `/${repoPath}/milestones` : null

  return (
    <div className={`${styles.buttons} ${styles.gap8} d-flex flex-wrap`}>
      {showLabelsAndMilestones && labelsPath && milestonesPath && (
        <div className={styles.hiddenWhenSidebarVisible}>
          <div className={`${styles.gap8} d-flex`}>
            <Button as={Link} to={labelsPath} leadingVisual={TagIcon}>
              Labels
            </Button>
            <Button as={Link} to={milestonesPath} leadingVisual={MilestoneIcon}>
              Milestones
            </Button>
          </div>
        </div>
      )}
      {!hideCreateButton &&
        (viewerCanCreateIssues ? (
          <CreateIssueButton
            label={BUTTON_LABELS.newIssue}
            navigate={navigate}
            optionConfig={{
              issueCreateArguments: {
                repository: scoped_repository
                  ? {
                      owner: scoped_repository.owner,
                      name: scoped_repository.name,
                    }
                  : undefined,
              },
              showRepositoryPicker: scoped_repository === null,
              emojiSkinTonePreference: current_user_settings?.preferred_emoji_skin_tone,
              showFullScreenButton: true,
              canBypassTemplateSelection: true,
              navigate,
            }}
          />
        ) : (
          <Tooltip text="Issue creation is restricted in this repository" direction="sw">
            <Button variant="primary" inactive>
              {BUTTON_LABELS.newIssue}
            </Button>
          </Tooltip>
        ))}
    </div>
  )
}
